import { supabase } from '../lib/supabase';

/**
 * Professional Client Notification Service
 * Handles SMS/email notifications for professional clients
 */

export const professionalClientNotificationService = {
  /**
   * Get notification preferences for authenticated professional client
   * @returns {Promise<Object>} Notification preferences
   */
  async getNotificationPreferences() {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Get professional client
      const { data: professionalClient, error: clientError } = await supabase
        ?.from('professional_clients')
        ?.select('id')
        ?.eq('auth_user_id', user?.id)
        ?.single();

      if (clientError) throw clientError;

      // Get notification preferences
      const { data, error } = await supabase
        ?.from('professional_client_notification_preferences')
        ?.select('*')
        ?.eq('professional_client_id', professionalClient?.id)
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        professionalClientId: data?.professional_client_id,
        emailEnabled: data?.email_enabled,
        smsEnabled: data?.sms_enabled,
        notifyShipmentStatus: data?.notify_shipment_status,
        notifyInvoiceReady: data?.notify_invoice_ready,
        notifyCertificateReady: data?.notify_certificate_ready,
        notifyContainerDeparture: data?.notify_container_departure,
        notifyPromotions: data?.notify_promotions,
        notificationEmail: data?.notification_email,
        notificationPhone: data?.notification_phone,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      throw error;
    }
  },

  /**
   * Update notification preferences
   * @param {Object} preferences - Updated preferences
   * @returns {Promise<Object>} Updated preferences
   */
  async updateNotificationPreferences(preferences) {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Get professional client
      const { data: professionalClient, error: clientError } = await supabase
        ?.from('professional_clients')
        ?.select('id')
        ?.eq('auth_user_id', user?.id)
        ?.single();

      if (clientError) throw clientError;

      // Update preferences
      const { data, error } = await supabase
        ?.from('professional_client_notification_preferences')
        ?.update({
          email_enabled: preferences?.emailEnabled,
          sms_enabled: preferences?.smsEnabled,
          notify_shipment_status: preferences?.notifyShipmentStatus,
          notify_invoice_ready: preferences?.notifyInvoiceReady,
          notify_certificate_ready: preferences?.notifyCertificateReady,
          notify_container_departure: preferences?.notifyContainerDeparture,
          notify_promotions: preferences?.notifyPromotions,
          notification_email: preferences?.notificationEmail,
          notification_phone: preferences?.notificationPhone,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('professional_client_id', professionalClient?.id)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        professionalClientId: data?.professional_client_id,
        emailEnabled: data?.email_enabled,
        smsEnabled: data?.sms_enabled,
        notifyShipmentStatus: data?.notify_shipment_status,
        notifyInvoiceReady: data?.notify_invoice_ready,
        notifyCertificateReady: data?.notify_certificate_ready,
        notifyContainerDeparture: data?.notify_container_departure,
        notifyPromotions: data?.notify_promotions,
        notificationEmail: data?.notification_email,
        notificationPhone: data?.notification_phone,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  },

  /**
   * Get notification history for authenticated professional client
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Notifications with pagination
   */
  async getNotifications({ page = 1, limit = 20, unreadOnly = false } = {}) {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Get professional client
      const { data: professionalClient, error: clientError } = await supabase
        ?.from('professional_clients')
        ?.select('id')
        ?.eq('auth_user_id', user?.id)
        ?.single();

      if (clientError) throw clientError;

      const offset = (page - 1) * limit;

      let query = supabase
        ?.from('professional_client_notifications')
        ?.select(`
          *,
          shipment:shipment_id (
            id,
            reference,
            current_status
          ),
          container:container_id (
            id,
            container_code,
            status
          )
        `, { count: 'exact' })
        ?.eq('professional_client_id', professionalClient?.id)
        ?.order('created_at', { ascending: false })
        ?.range(offset, offset + limit - 1);

      if (unreadOnly) {
        query = query?.eq('is_read', false);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        notifications: (data || [])?.map(notif => ({
          id: notif?.id,
          professionalClientId: notif?.professional_client_id,
          notificationType: notif?.notification_type,
          channel: notif?.channel,
          subject: notif?.subject,
          message: notif?.message,
          shipmentId: notif?.shipment_id,
          containerId: notif?.container_id,
          status: notif?.status,
          sentAt: notif?.sent_at,
          errorMessage: notif?.error_message,
          externalId: notif?.external_id,
          isRead: notif?.is_read,
          readAt: notif?.read_at,
          createdAt: notif?.created_at,
          shipment: notif?.shipment ? {
            id: notif?.shipment?.id,
            reference: notif?.shipment?.reference,
            currentStatus: notif?.shipment?.current_status
          } : null,
          container: notif?.container ? {
            id: notif?.container?.id,
            containerCode: notif?.container?.container_code,
            status: notif?.container?.status
          } : null
        })),
        total: count || 0,
        page,
        limit,
        totalPages: Math.ceil((count || 0) / limit)
      };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  /**
   * Get unread notifications count
   * @returns {Promise<number>} Number of unread notifications
   */
  async getUnreadCount() {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Get professional client
      const { data: professionalClient, error: clientError } = await supabase
        ?.from('professional_clients')
        ?.select('id')
        ?.eq('auth_user_id', user?.id)
        ?.single();

      if (clientError) throw clientError;

      const { count, error } = await supabase
        ?.from('professional_client_notifications')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('professional_client_id', professionalClient?.id)
        ?.eq('is_read', false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  },

  /**
   * Mark notification as read
   * @param {string} notificationId - Notification ID
   * @returns {Promise<Object>} Updated notification
   */
  async markAsRead(notificationId) {
    try {
      const { data, error } = await supabase
        ?.from('professional_client_notifications')
        ?.update({
          is_read: true,
          read_at: new Date()?.toISOString()
        })
        ?.eq('id', notificationId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  /**
   * Mark multiple notifications as read
   * @param {Array<string>} notificationIds - Array of notification IDs
   * @returns {Promise<Array>} Updated notifications
   */
  async markMultipleAsRead(notificationIds) {
    try {
      const { data, error } = await supabase
        ?.from('professional_client_notifications')
        ?.update({
          is_read: true,
          read_at: new Date()?.toISOString()
        })
        ?.in('id', notificationIds)
        ?.select();

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error marking multiple notifications as read:', error);
      throw error;
    }
  },

  /**
   * Subscribe to real-time notification updates
   * @param {Function} callback - Callback function for updates
   * @returns {Object} Subscription object
   */
  subscribeToNotifications(callback) {
    const channel = supabase
      ?.channel('professional-client-notifications-realtime')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'professional_client_notifications'
        },
        (payload) => {
          callback({
            eventType: payload?.eventType,
            new: payload?.new,
            old: payload?.old
          });
        }
      )
      ?.subscribe();

    return {
      unsubscribe: () => {
        supabase?.removeChannel(channel);
      }
    };
  },

  /**
   * Send test notification (for testing purposes)
   * @param {string} channel - 'email' or 'sms'
   * @returns {Promise<Object>} Result
   */
  async sendTestNotification(channel) {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Get professional client
      const { data: professionalClient, error: clientError } = await supabase
        ?.from('professional_clients')
        ?.select('*')
        ?.eq('auth_user_id', user?.id)
        ?.single();

      if (clientError) throw clientError;

      // Get preferences
      const { data: prefs, error: prefsError } = await supabase
        ?.from('professional_client_notification_preferences')
        ?.select('*')
        ?.eq('professional_client_id', professionalClient?.id)
        ?.single();

      if (prefsError) throw prefsError;

      if (channel === 'email') {
        // Call email edge function
        const { data, error } = await supabase?.functions?.invoke('send-professional-email-notification', {
          body: {
            email: prefs?.notification_email || professionalClient?.email,
            companyName: professionalClient?.company_name,
            subject: 'Test de notification - ADT Express',
            message: 'Ceci est un message de test pour vérifier que vos notifications par email fonctionnent correctement.',
            notificationType: 'promotion',
            entityId: null
          }
        });

        if (error) throw error;
        return { success: true, channel: 'email', data };
      } else if (channel === 'sms') {
        if (!prefs?.notification_phone) {
          throw new Error('Numéro de téléphone non configuré');
        }

        // Call SMS edge function
        const { data, error } = await supabase?.functions?.invoke('send-professional-sms-notification', {
          body: {
            to: prefs?.notification_phone,
            message: `ADT Express: Test de notification SMS. Vos alertes SMS sont activées pour ${professionalClient?.company_name}.`,
            notificationType: 'promotion',
            entityId: null
          }
        });

        if (error) throw error;
        return { success: true, channel: 'sms', data };
      }

      throw new Error('Canal invalide');
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  }
};